import React, { useState } from 'react';
import { toast } from 'react-toastify';
import useDarkMode from '../../hooks/useDarkMode';

const SettingsPage = () => {
  // Get dark mode state from custom hook
  const { darkMode, setDarkMode } = useDarkMode();
  
  // Load other settings from localStorage or use defaults
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    return localStorage.getItem('notificationsEnabled') === 'true' || true;
  });
  const [autoScanEnabled, setAutoScanEnabled] = useState(() => {
    return localStorage.getItem('autoScanEnabled') === 'true' || true;
  });
  
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Settings</h1>
      
      <div className="bg-white rounded-lg shadow p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2">Application Settings</h2>
          <div className="border-t border-gray-200 pt-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Notifications</h3>
                <p className="text-sm text-gray-500">Receive notifications about inventory updates and alerts</p>
              </div>
              <div className="flex items-center">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={notificationsEnabled} 
                    onChange={() => setNotificationsEnabled(!notificationsEnabled)} 
                    className="sr-only peer" 
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-primary-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Dark Mode</h3>
                <p className="text-sm text-gray-500">Use dark theme for the application interface</p>
              </div>
              <div className="flex items-center">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={darkMode} 
                    onChange={() => setDarkMode(!darkMode)} 
                    className="sr-only peer" 
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-primary-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2">Scanner Settings</h2>
          <div className="border-t border-gray-200 pt-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Auto-scan Mode</h3>
                <p className="text-sm text-gray-500">Automatically scan when barcode is detected</p>
              </div>
              <div className="flex items-center">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={autoScanEnabled} 
                    onChange={() => setAutoScanEnabled(!autoScanEnabled)} 
                    className="sr-only peer" 
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-primary-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>
            </div>
            
            <div className="pt-2">
              <h3 className="font-medium mb-2">Default Barcode Type</h3>
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500">
                <option>UPC-A</option>
                <option>EAN-13</option>
                <option>Code-128</option>
                <option>QR Code</option>
                <option>All Types</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2">Data Management</h2>
          <div className="border-t border-gray-200 pt-4 space-y-4">
            <div>
              <h3 className="font-medium mb-2">Export Frequency</h3>
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500">
                <option>Daily</option>
                <option>Weekly</option>
                <option>Monthly</option>
                <option>Manual Only</option>
              </select>
            </div>
            
            <div className="pt-2">
              <h3 className="font-medium mb-2">Data Backup</h3>
              <button className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500">
                Download Backup
              </button>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button 
            className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            onClick={() => {
              // Reset to saved values from localStorage or defaults
              setNotificationsEnabled(localStorage.getItem('notificationsEnabled') === 'true' || true);
              // Dark mode is handled by the hook, it will be restored from localStorage automatically
              setAutoScanEnabled(localStorage.getItem('autoScanEnabled') === 'true' || true);
            }}
          >
            Cancel
          </button>
          <button 
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
            onClick={() => {
              // Save settings to localStorage
              localStorage.setItem('notificationsEnabled', notificationsEnabled);
              localStorage.setItem('autoScanEnabled', autoScanEnabled);
              // Dark mode is saved automatically by the hook
              
              // Show success message
              toast.success('Settings saved successfully');
            }}
          >
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
